import { TransactionSearchDrawer } from "@components/transactions/TransactionSearchDrawer/TransactionSearchDrawer";
import { useCallback, useContext, useMemo } from "react";
import { monetaryValuesEqual } from "@common/helpers/monetary";
import {
    getStatmentTransactionId,
    isDateOnlyInReconciledPeriod,
    isDateOnlyOutsideStatementPeriod,
} from "@common/helpers/reconciliation";
import {
    canCashItemClearOnDate,
    getCashItemLabel,
    getTransactionDatedBeforeCashItemMessage,
} from "@common/helpers/reconciliationCashItems";
import { FinancialAccount } from "@common/types/financialAccount";
import { useLockDate } from "@api/lock.api";
import { isLockedByDateOnly } from "@helpers/lock";
import { toUTCDateOnly } from "@common/helpers/date";
import {
    ReconciliationSummaryResponse,
    TransactionForReconciliationFromStatement,
} from "@common/contracts/reconciliation.contract";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ReconciliationContext } from "./Reconciliation.context";
import { ReconciliationTransactionPreview } from "./ReconciliationTransactionPreview";
import { buildMatchSearchView } from "./matchSearchView";
import { useStatementLineRows } from "./useStatementLineRows";
import { getNewDateByTransactionId } from "./workflowHelpers";
import styles from "./Reconciliation.module.scss";

interface Props {
    financialAccount: FinancialAccount;
    summary: ReconciliationSummaryResponse;
    previousClosingDate?: string;
    statementLinesToAdd?: TransactionForReconciliationFromStatement[];
}

export const ReconciliationFindCheckMatch: React.FC<Props> = ({
    financialAccount,
    summary,
    previousClosingDate,
    statementLinesToAdd = [],
}) => {
    const {
        checkToMatch,
        checkMerges,
        setCheckToMatch,
        registerCheckMerge,
        financialTransactionIdsIgnoringDateChanges,
        financialTransactionsToRemoveIdsSet,
    } = useContext(ReconciliationContext);
    const newDateByTransactionId = useMemo(
        () =>
            getNewDateByTransactionId({
                pairs: summary.transactionsPairs,
                financialTransactionIdsIgnoringDateChanges,
            }),
        [summary.transactionsPairs, financialTransactionIdsIgnoringDateChanges],
    );
    const getReconciledDateOnly = useCallback(
        (transaction: TransactionListItem) =>
            newDateByTransactionId.get(transaction.id) ?? transaction.dateOnly,
        [newDateByTransactionId],
    );
    const lockDate = useLockDate(financialAccount.entityId);
    const lockDateOnly = lockDate ? toUTCDateOnly(lockDate) : undefined;
    const itemLabel = getCashItemLabel(checkToMatch?.type);
    const {
        rows: statementLineRows,
        findStatementLine,
        getStatementLineDisabledReason,
    } = useStatementLineRows({
        checkToMatch,
        statementLines: statementLinesToAdd,
        financialAccount,
        lockDate,
        openingDate: summary.statementsOpeningDate,
        closingDate: summary.statementsClosingDate,
    });

    const labelGetter = useCallback(
        () => `Cleared this ${itemLabel}`,
        [itemLabel],
    );

    const isUsedByAnotherCheck = useCallback(
        (transaction: TransactionListItem) =>
            checkMerges.some(
                (merge) =>
                    "bankTransactionId" in merge &&
                    merge.bankTransactionId === transaction.id &&
                    merge.checkTransactionId !==
                        checkToMatch?.financialTransactionId,
            ),
        [checkMerges, checkToMatch],
    );

    const isDisabled = useCallback(
        (transaction: TransactionListItem) => {
            const statementLine = findStatementLine(transaction);
            if (statementLine) {
                return getStatementLineDisabledReason(statementLine) != null;
            }
            const dateOnly = getReconciledDateOnly(transaction);
            return (
                checkToMatch == null ||
                !monetaryValuesEqual(
                    transaction.originalAmount,
                    checkToMatch.amount,
                ) ||
                !canCashItemClearOnDate({
                    cashItemDateOnly: checkToMatch.date,
                    clearedDateOnly: dateOnly,
                }) ||
                isDateOnlyOutsideStatementPeriod({
                    dateOnly,
                    openingDate: summary.statementsOpeningDate,
                    closingDate: summary.statementsClosingDate,
                }) ||
                isLockedByDateOnly(lockDate, transaction.dateOnly) ||
                (dateOnly != null &&
                    isDateOnlyInReconciledPeriod({
                        dateOnly,
                        previousClosingDate,
                    })) ||
                transaction.financialAccountId !== financialAccount.id ||
                Boolean(transaction.reconciledInId) ||
                financialTransactionsToRemoveIdsSet.has(transaction.id) ||
                isUsedByAnotherCheck(transaction)
            );
        },
        [
            checkToMatch,
            findStatementLine,
            getStatementLineDisabledReason,
            getReconciledDateOnly,
            lockDate,
            previousClosingDate,
            financialAccount.id,
            financialTransactionsToRemoveIdsSet,
            isUsedByAnotherCheck,
            summary.statementsOpeningDate,
            summary.statementsClosingDate,
        ],
    );

    const tooltipGetter = useCallback(
        (transaction: TransactionListItem) => {
            const statementLine = findStatementLine(transaction);
            if (statementLine) {
                return getStatementLineDisabledReason(statementLine);
            }
            if (
                checkToMatch == null ||
                !monetaryValuesEqual(
                    transaction.originalAmount,
                    checkToMatch.amount,
                )
            ) {
                return "Transaction amount is different";
            }
            const dateOnly = getReconciledDateOnly(transaction);
            if (dateOnly == null) {
                return "Transaction date is missing";
            }
            if (
                !canCashItemClearOnDate({
                    cashItemDateOnly: checkToMatch.date,
                    clearedDateOnly: dateOnly,
                })
            ) {
                return getTransactionDatedBeforeCashItemMessage(
                    checkToMatch.type,
                );
            }
            if (
                isDateOnlyOutsideStatementPeriod({
                    dateOnly,
                    openingDate: summary.statementsOpeningDate,
                    closingDate: summary.statementsClosingDate,
                })
            ) {
                return "Transaction is outside the statement period";
            }
            if (isLockedByDateOnly(lockDate, transaction.dateOnly)) {
                return "Transaction is locked";
            }
            if (
                isDateOnlyInReconciledPeriod({
                    dateOnly,
                    previousClosingDate,
                })
            ) {
                return "Transaction is in an already reconciled period";
            }
            if (transaction.financialAccountId !== financialAccount.id) {
                return "Transaction belongs to a different account than the reconciled one";
            }
            if (transaction.reconciledInId) {
                return "Transaction is already reconciled against another statement";
            }
            if (financialTransactionsToRemoveIdsSet.has(transaction.id)) {
                return "Transaction will be deleted when you finish";
            }
            if (isUsedByAnotherCheck(transaction)) {
                return "Transaction is already clearing another check or deposit";
            }
        },
        [
            checkToMatch,
            findStatementLine,
            getStatementLineDisabledReason,
            getReconciledDateOnly,
            lockDate,
            previousClosingDate,
            financialAccount.id,
            financialTransactionsToRemoveIdsSet,
            isUsedByAnotherCheck,
            summary.statementsOpeningDate,
            summary.statementsClosingDate,
        ],
    );

    const onCTAClick = useCallback(
        (transaction: TransactionListItem) => {
            if (!checkToMatch || isDisabled(transaction)) {
                return;
            }

            const statementLine = findStatementLine(transaction);
            registerCheckMerge({
                checkTransactionId: checkToMatch.financialTransactionId,
                bankDescription: transaction.bankDescription,
                ...(statementLine
                    ? {
                          statementTransactionId:
                              getStatmentTransactionId(statementLine),
                      }
                    : { bankTransactionId: transaction.id }),
            });
            setCheckToMatch(undefined);
        },
        [
            checkToMatch,
            findStatementLine,
            isDisabled,
            registerCheckMerge,
            setCheckToMatch,
        ],
    );

    const checkToMatchTransactionId = checkToMatch?.financialTransactionId;
    const excludedTransactionIds = useMemo(
        () =>
            checkToMatchTransactionId != null
                ? [checkToMatchTransactionId]
                : [],
        [checkToMatchTransactionId],
    );

    const view = useMemo(
        () =>
            buildMatchSearchView({
                financialAccount,
                amount: checkToMatch?.amount,
                openingDate: summary.statementsOpeningDate,
                closingDate: summary.statementsClosingDate,
                additionalDays: summary.additionalDays,
                previousClosingDate,
                lockDateOnly,
                notBeforeDateOnly: checkToMatch?.date,
                excludedTransactionIds,
                labelGetter,
                isDisabled,
                tooltipGetter,
                onCTAClick,
            }),
        [
            checkToMatch,
            excludedTransactionIds,
            labelGetter,
            isDisabled,
            tooltipGetter,
            financialAccount,
            onCTAClick,
            summary.statementsOpeningDate,
            summary.statementsClosingDate,
            summary.additionalDays,
            previousClosingDate,
            lockDateOnly,
        ],
    );

    return (
        <TransactionSearchDrawer
            open={!!checkToMatch}
            onClose={() => setCheckToMatch(undefined)}
            view={view}
            pinnedRows={statementLineRows}
            showEntityFilter={false}
            showActionsFilter={false}
            header={
                <span className="font-weight-medium">
                    {`Find the transaction that cleared this ${itemLabel}`}
                </span>
            }
            addon={
                checkToMatch && (
                    <ReconciliationTransactionPreview
                        label={`Outstanding ${itemLabel}:`}
                        transaction={checkToMatch}
                    />
                )
            }
            contentClassName={styles.findTransactionContent}
        />
    );
};
